import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Flex,
  ModalBody,
  useToast,
  FormControl,
  FormLabel,
  Text,
  Input,
  Switch,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { SubmitButton } from '../shared/form/SubmitButton';
import { useTranslation } from 'react-i18next';
import {
  CreateUserRequest,
  Role,
  SlimUser,
  userApi,
} from '@texas/api/endpoints/userApi';
import { ExistingUserLinks } from './ExistingUserLinks';
import { contactsApi } from '@texas/api/endpoints/contactsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { roleFriendlyName } from '@texas/utils/helpers/roleHelpers';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { Controller, useForm } from 'react-hook-form';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ReactSelectOption } from '@texas/types';
import { ErrorLabel } from '../shared/ErrorLabel';
import { TexasSelect } from '../shared/form/TexasSelect';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { contactEvents } from '@texas/components/contacts/events';
import { userEvents } from './events';
import { timezones } from '@texas/resources/countries/timezones';
import { SharedDisclosureProps } from '../shared/types';

export interface CreateExternalUserProps extends SharedDisclosureProps {
  customerId?: number;
  role: Role.Brand | Role.Supplier;
  referenceName: string;
  email?: string;
  name?: string;
  contactId?: number;
  onCreated?: (user: SlimUser) => void;
}

export function CreateExternalUser({
  customerId,
  role,
  referenceName,
  email,
  name,
  contactId,
  isOpen,
  onClose,
  onCreated,
}: CreateExternalUserProps) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('user.createExternalBrandUser', {
            name: referenceName,
            interpolation: { escapeValue: false },
          })}
        </ModalHeader>
        <ModalCloseButton />
        <InnerModal
          onClose={onClose}
          customerId={customerId}
          role={role}
          email={email}
          name={name}
          onCreated={onCreated}
          contactId={contactId}
        />
      </ModalContent>
    </Modal>
  );
}

function InnerModal({
  onClose,
  customerId,
  role,
  email,
  name,
  contactId,
  onCreated,
}: {
  onClose: () => void;
  customerId?: number;
  role: Role.Brand | Role.Supplier;
  email?: string;
  name?: string;
  contactId?: number;
  onCreated?: (user: SlimUser) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation();

  const { request: createRequest, error } = useApiRequest(userApi.createUser);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting, isValid },
    reset,
    watch,
  } = useForm<CreateUserRequest>({
    defaultValues: {
      role,
      customerId: customerId,
      email: email ?? '',
      name,
      contactId,
      passwordLoginEnabled: true,
      rudholmIdentityLoginEnabled: false,
    },
  });

  const normalizedEmail = watch('email').trim().toLowerCase();
  const [debouncedEmail] = useDebounce(normalizedEmail, 300);
  const [checkedEmail, setCheckedEmail] = useState<string | null>(null);
  const lookupEnabled = role === Role.Supplier && !!contactId;
  const {
    data: existingUser,
    refetch: findExistingUser,
    loading: lookupLoading,
    error: lookupError,
    set: setExistingUser,
  } = useApiResource(contactsApi.getExistingSupplierUser);
  const { request: linkUser, error: linkError } = useApiRequest(
    contactsApi.linkSupplierUser,
  );
  const lookupReady =
    !lookupEnabled ||
    (checkedEmail === normalizedEmail && !lookupLoading && !lookupError);
  const matchingUser = lookupReady && existingUser ? existingUser : null;

  useEffect(() => {
    if (!lookupEnabled || !contactId) return;
    setCheckedEmail(null);
    setExistingUser(null);
    if (!/^[^\s@]+@[^\s@]+$/.test(debouncedEmail)) return;
    let cancelled = false;
    findExistingUser(contactId, debouncedEmail).then(() => {
      if (!cancelled) setCheckedEmail(debouncedEmail);
    });
    return () => {
      cancelled = true;
    };
  }, [
    contactId,
    debouncedEmail,
    findExistingUser,
    lookupEnabled,
    setExistingUser,
  ]);

  const onLink = async () => {
    if (!contactId || !matchingUser?.canLink) return;
    await request(
      linkUser,
      [contactId, matchingUser.id, normalizedEmail],
      (user) => {
        toast({
          title: t('contacts.userLinked'),
          status: 'success',
          isClosable: true,
        });
        contactEvents.userConnected.dispatch(contactId);
        contactEvents.userLinksChanged.dispatch(user.id);
        onCreated?.(user);
        onClose();
      },
    );
  };

  const onSubmit = async (data: CreateUserRequest) => {
    if (!lookupReady) return;
    if (matchingUser) {
      await onLink();
      return;
    }
    await request(createRequest, [data], (user) => {
      toast({
        title: t('general.created'),
        status: 'success',
        isClosable: true,
      });
      if (user.welcomeEmailFailed)
        toast({
          title: t('user.welcomeEmailFailed'),
          status: 'warning',
          isClosable: true,
          duration: null,
        });
      reset({
        email: '',
        role,
        customerId: customerId,
        passwordLoginEnabled: true,
        rudholmIdentityLoginEnabled: false,
      });
      onClose();
      userEvents.newUserCreated.dispatch();
      if (contactId) contactEvents.userConnected.dispatch(contactId);
      contactEvents.userLinksChanged.dispatch(user.id);
      if (onCreated) onCreated(user);
    });
  };

  return (
    <>
      {!matchingUser && error && <ErrorDetails error={error} />}
      {lookupError && <ErrorDetails error={lookupError} />}
      {linkError && <ErrorDetails error={linkError} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody as={Flex} gap={2} flexDir="column">
          {matchingUser && (
            <Alert status={matchingUser.canLink ? 'info' : 'warning'}>
              <AlertIcon />
              <Box>
                <AlertTitle>{t('contacts.existingUserFound')}</AlertTitle>
                <AlertDescription>
                  <Text>
                    {matchingUser.name} · {matchingUser.email}
                  </Text>
                  {matchingUser.role && (
                    <Text>
                      {roleFriendlyName(
                        ensureEnumNumber(Role, matchingUser.role),
                        t,
                      )}
                    </Text>
                  )}
                  <Text>
                    {t(
                      matchingUser.archived
                        ? 'contacts.existingUserArchived'
                        : matchingUser.canLink
                        ? 'contacts.existingUserLinkInfo'
                        : 'contacts.existingUserIncompatible',
                    )}
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>
          )}
          {matchingUser && <ExistingUserLinks user={matchingUser} />}
          <Flex gap={2}>
            <FormControl isInvalid={!!errors.email?.message}>
              <FormLabel>{t('general.email')}</FormLabel>
              <Input
                type="email"
                {...register('email', { required: true })}
                variant="texas"
              />
              <ErrorLabel text={errors.email?.message} />
            </FormControl>
            {!matchingUser && (
              <FormControl isInvalid={!!errors.name?.message}>
                <FormLabel>{t('general.name')}</FormLabel>
                <Input
                  {...register('name', { required: true })}
                  variant="texas"
                />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>
            )}
          </Flex>
          {!matchingUser && (
            <Box>
              <FormControl pb={2}>
                <FormLabel>{t('general.timeZone')}</FormLabel>
                <Controller
                  name="timeZone"
                  control={control}
                  render={({ field }) => (
                    <TexasSelect
                      {...field}
                      value={timezones
                        .map((x) => ({
                          label: `${x.name} ${x.utc}`,
                          value: x.zone,
                        }))
                        .find((x) => x.value === field.value)}
                      onChange={(value) => field.onChange(value?.value ?? null)}
                      isClearable={true}
                      options={timezones.map<ReactSelectOption>((u) => ({
                        label: `${u.name} ${u.utc}`,
                        value: u.zone,
                      }))}
                    />
                  )}
                />
              </FormControl>
              <Text fontSize="md" fontWeight="bold">
                {t('user.sendWelcomeEmail')}
              </Text>
              <Text
                fontSize="sm"
                color="gray.200"
                _light={{ color: 'gray.600' }}
              >
                {t('user.sendWelcomeMailDesc')}
              </Text>
              <FormControl display="flex" alignItems="center" pt={2} gap={2}>
                <Controller
                  name="sendWelcomeMail"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      isChecked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
                <FormLabel mb="0">{t('user.sendWelcomeMailToggle')}</FormLabel>
              </FormControl>
            </Box>
          )}
        </ModalBody>
        <ModalFooter
          bg="texas.bg.950"
          borderBottomRadius="md"
          gap={6}
          alignItems="end"
          _light={{ bg: 'gray.50' }}
        >
          <Flex w="fit-content" justify="flex-end">
            <SubmitButton
              disabled={
                !isValid ||
                !lookupReady ||
                (matchingUser !== null && !matchingUser.canLink)
              }
              loading={isSubmitting || lookupLoading}
            >
              {t(
                matchingUser ? 'contacts.linkExistingUser' : 'user.createUser',
              )}
            </SubmitButton>
          </Flex>
        </ModalFooter>
      </form>
    </>
  );
}
